/**
 * MCP tool-call activity viewer (`/logs?tab=mcp`, embedded in the Log Center).
 *
 * Owner/team_lead-only window into every MCP tool call the organisation made —
 * reads AND writes, across the official platform MCPs and any custom client
 * MCP servers. Mirrors the Meta API activity tab's structure: a filter bar
 * (brand / platform / direction / status / date range) and a paginated table
 * driven by the response `meta` block.
 *
 * Backend: GET /api/v1/agency/mcp-calls (org-scoped, owner/team_lead). Each row
 * carries { platform, connection_id, tool_name, direction, status, error_code,
 * duration_ms, employee, shop, created_at }.
 *
 * Shell matches `MetaActivity.tsx` / `AuditLog.tsx`:
 *   PageWrapperMain → subPage → SubPageContentHolder → SubPageInnerContentHolder.
 * NO MUI primitives — plain divs + custom SCSS (`mcp-calls/mcpCalls.scss`) so
 * the page picks up the legacy theme variables. Status/direction use the shared
 * `StatusPill`.
 *
 * Role-gated to the `mcp_calls.view` capability (owner/team_lead). Other roles
 * see an inline "no permission" message inside the legacy chrome.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/mcp-calls/mcpCalls.scss';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import StatusPill, { StatusTone } from '../../ui/statusPill/StatusPill';
import TeachingEmptyState from '../../components/emptyState/TeachingEmptyState';
import { RootState } from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import { useCan, useAccessibleShops } from '../../lib/rbac';
import { databaseLargeSVG } from '../../lib/allCustomSVGs';
import {
  AgencyEmployee,
  listEmployees,
} from '../../lib/api/identity/identity-api';
import {
  McpCallEntry,
  McpCallMeta,
  McpCallFilters,
  McpCallDirection,
  McpCallStatus,
  listMcpCalls,
} from '../../lib/api/mcp-calls/mcp-calls-api';

const PER_PAGE = 25;

const DIRECTION_OPTIONS: McpCallDirection[] = ['read', 'write'];
const STATUS_OPTIONS: McpCallStatus[] = ['ok', 'denied', 'error', 'proposed'];

// ---------- helpers ----------

/** Format an ISO timestamp into a compact, locale-aware string. */
function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format a millisecond duration in a humane way (<1s shows "Nms"). */
function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remSeconds}s`;
}

/** Map a per-call status to a shared StatusPill tone. */
function statusTone(status: McpCallStatus): StatusTone {
  switch (status) {
    case 'ok':
      return 'success';
    case 'denied':
      return 'warning';
    // A write diverted into the approval pipeline (visible in the Action Queue).
    case 'proposed':
      return 'info';
    case 'error':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Map a direction to a shared StatusPill tone (read=info, write=neutral). */
function directionTone(direction: McpCallDirection): StatusTone {
  return direction === 'write' ? 'warning' : 'info';
}

// ---------- sub-components ----------

const Pagination: React.FC<{
  meta: McpCallMeta;
  onPageChange: (page: number) => void;
  loading: boolean;
}> = ({ meta, onPageChange, loading }) => {
  const { t } = useTranslation();
  const current = meta.current_page || 1;
  const last = Math.max(1, meta.last_page || 1);
  const from = meta.total === 0 ? 0 : (current - 1) * meta.per_page + 1;
  const to = Math.min(meta.total, current * meta.per_page);

  return (
    <div className='mcpCallsPagination'>
      <div className='mcpCallsPagination__info'>
        {t('McpCallsPaginationInfo', {
          from,
          to,
          total: meta.total,
        })}
      </div>
      <div className='mcpCallsPagination__controls'>
        <button
          type='button'
          className='mcpCallsPagination__btn'
          disabled={loading || current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          {t('Previous')}
        </button>
        <span className='mcpCallsPagination__page'>
          {t('McpCallsPageOf', { current, last })}
        </span>
        <button
          type='button'
          className='mcpCallsPagination__btn'
          disabled={loading || current >= last}
          onClick={() => onPageChange(current + 1)}
        >
          {t('Next')}
        </button>
      </div>
    </div>
  );
};

// ---------- main ----------

const McpCalls: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const canView = useCan('mcp_calls.view');
  const accessibleShops = useAccessibleShops();

  // Resolve the accessible-shop list (owner sees all → sourced from the
  // identity payload). Used for the brand filter dropdown AND to humanise shop
  // ids/slugs in rows that lack a `shop_name`. Mirrors MetaActivity.tsx.
  const identity = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity
  );
  const shopOptions = useMemo(() => {
    const fromShops = (identity?.accessible_shops ?? []).map((s) => ({
      slug: s.slug ?? s.brand_slug ?? '',
      name: s.name ?? s.brand_name ?? s.slug ?? s.brand_slug ?? '',
    }));
    const fromBrands = (identity?.accessible_brands ?? []).map((b) => ({
      slug: b.brand_slug ?? '',
      name: b.brand_name ?? b.brand_slug ?? '',
    }));
    const merged = [...fromShops, ...fromBrands].filter((s) => s.slug);
    const seen = new Set<string>();
    return merged.filter((s) => {
      if (seen.has(s.slug)) return false;
      seen.add(s.slug);
      if (accessibleShops === null) return true; // owner: all
      return accessibleShops.includes(s.slug);
    });
  }, [identity, accessibleShops]);

  // Employee lookup — fetched once on mount, used to humanise the employee
  // column when a row only carries `employee_id`. Mirrors AuditLog.tsx.
  const [employees, setEmployees] = useState<AgencyEmployee[]>([]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    listEmployees()
      .then((list) => {
        if (!cancelled) setEmployees(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[McpCalls] failed to load employees', err);
        if (!cancelled) setEmployees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  // Best-effort employee-email lookup (fallback when a row predates the
  // backend's employee_email enrichment). Seeded with the acting employee and
  // enriched with the full employee list once it loads.
  const employeeEmailById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (identity?.employee_id) {
      const seed = identity.email || identity.name;
      if (seed) map[String(identity.employee_id)] = seed;
    }
    employees.forEach((emp) => {
      if (emp.id) map[String(emp.id)] = emp.email || emp.name || String(emp.id);
    });
    return map;
  }, [identity, employees]);

  // ----- filter state (drives the calls table) -----
  const [shop, setShop] = useState('');
  const [platform, setPlatform] = useState('');
  const [direction, setDirection] = useState<McpCallDirection | ''>('');
  const [status, setStatus] = useState<McpCallStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  // ----- table state -----
  const [entries, setEntries] = useState<McpCallEntry[]>([]);
  const [meta, setMeta] = useState<McpCallMeta>({
    total: 0,
    per_page: PER_PAGE,
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTable = useCallback(
    async (overridePage?: number) => {
      setLoading(true);
      setError(null);
      const filters: McpCallFilters = {
        shop: shop || undefined,
        platform: platform || undefined,
        direction: direction || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        page: overridePage ?? page,
        per_page: PER_PAGE,
      };
      try {
        const res = await listMcpCalls(filters);
        setEntries(res.data);
        setMeta(res.meta);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[McpCalls] table load failed', err);
        const msg =
          err instanceof Error ? err.message : 'Failed to load MCP calls.';
        setError(msg);
        dispatch(setMessage({ message: msg, messageType: 'error' }));
      } finally {
        setLoading(false);
      }
    },
    [shop, platform, direction, status, from, to, page, dispatch]
  );

  // Table load: on mount + page changes. Filter changes go through
  // `applyFilters` (which resets the page to 1) to avoid double-fetching.
  useEffect(() => {
    if (canView) {
      loadTable(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, page]);

  const applyFilters = () => {
    if (page === 1) {
      loadTable(1);
    } else {
      setPage(1); // triggers the effect above
    }
  };

  const clearFilters = () => {
    setShop('');
    setPlatform('');
    setDirection('');
    setStatus('');
    setFrom('');
    setTo('');
    if (page === 1) {
      setTimeout(() => loadTable(1), 0);
    } else {
      setPage(1);
    }
  };

  const handlePageChange = (next: number) => {
    setPage(next);
  };

  /* ------------------------------------------------------------
     Shell wrapper. When `embedded` (hosted inside the Log Center
     tabbed page) we render only the inner content — the Log Center
     owns the PageWrapperMain/SubPage chrome and the title. Standalone
     keeps the full shell (consistent with the sibling tabs).
     ------------------------------------------------------------ */
  const wrapShell = (content: React.ReactNode) =>
    embedded ? (
      <>{content}</>
    ) : (
      <PageWrapperMain>
        <div className='subPage'>
          <SubPageContentHolder>
            <SubPageInnerContentHolder
              title={t('McpCalls')}
              subTitle={t('McpCallsSubTitle')}
              backgroundColor={'var(--main-background-color)'}
            >
              {content}
            </SubPageInnerContentHolder>
          </SubPageContentHolder>
        </div>
      </PageWrapperMain>
    );

  /* ============================================================
     No-access state — render the legacy shell so the sidebar stays
     mounted and the user can navigate away.
     ============================================================ */
  if (!canView) {
    return wrapShell(
      <div className='mcpCallsPage'>
        <div className='mcpCallsPage__noAccess'>{t('McpCallsNoAccess')}</div>
      </div>
    );
  }

  /* ============================================================
     Main view
     ============================================================ */
  return wrapShell(
    <div className='mcpCallsPage'>
      {/* ---------- Filter bar ---------- */}
      <div className='mcpCallsFilters'>
        <div className='mcpCallsFilters__row'>
          <div className='mcpCallsFilters__field'>
            <label className='mcpCallsFilters__label'>
              {t('McpCallsFilterShop')}
            </label>
            <select
              className='mcpCallsFilters__select'
              value={shop}
              onChange={(e) => setShop(e.target.value)}
            >
              <option value=''>{t('McpCallsAllShops')}</option>
              {shopOptions.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className='mcpCallsFilters__field'>
            <label className='mcpCallsFilters__label'>
              {t('McpCallsFilterPlatform')}
            </label>
            <input
              className='mcpCallsFilters__input'
              type='text'
              value={platform}
              placeholder={t('McpCallsPlatformPlaceholder')}
              onChange={(e) => setPlatform(e.target.value)}
            />
          </div>

          <div className='mcpCallsFilters__field'>
            <label className='mcpCallsFilters__label'>
              {t('McpCallsFilterDirection')}
            </label>
            <select
              className='mcpCallsFilters__select'
              value={direction}
              onChange={(e) =>
                setDirection(e.target.value as McpCallDirection | '')
              }
            >
              <option value=''>{t('McpCallsAllDirections')}</option>
              {DIRECTION_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {t(`McpCallsDirection_${d}`)}
                </option>
              ))}
            </select>
          </div>

          <div className='mcpCallsFilters__field'>
            <label className='mcpCallsFilters__label'>
              {t('McpCallsFilterStatus')}
            </label>
            <select
              className='mcpCallsFilters__select'
              value={status}
              onChange={(e) => setStatus(e.target.value as McpCallStatus | '')}
            >
              <option value=''>{t('McpCallsAllStatuses')}</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`McpCallsStatus_${s}`)}
                </option>
              ))}
            </select>
          </div>

          <div className='mcpCallsFilters__field'>
            <label className='mcpCallsFilters__label'>
              {t('McpCallsFilterFrom')}
            </label>
            <input
              className='mcpCallsFilters__input'
              type='date'
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className='mcpCallsFilters__field'>
            <label className='mcpCallsFilters__label'>
              {t('McpCallsFilterTo')}
            </label>
            <input
              className='mcpCallsFilters__input'
              type='date'
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        <div className='mcpCallsFilters__actions'>
          <MainSquareButton
            value={t('McpCallsApplyFilters')}
            type='button'
            normal
            onClick={applyFilters}
          />
          <button
            type='button'
            className='mcpCallsFilters__clearBtn'
            onClick={clearFilters}
          >
            {t('McpCallsClearFilters')}
          </button>
        </div>
      </div>

      {error && (
        <div className='mcpCallsPage__error' role='alert'>
          <span>{error}</span>
          <button
            type='button'
            className='mcpCallsPage__retryBtn'
            onClick={() => loadTable(page)}
          >
            {t('Retry')}
          </button>
        </div>
      )}

      {/* ---------- Calls table ---------- */}
      {loading && entries.length === 0 ? (
        <div className='mcpCallsPage__loading'>
          <LoadingSpinner customLoadingMsg={t('LoadingMcpCalls')} />
        </div>
      ) : (
        <div className='mcpCallsTable'>
          <div className='mcpCallsTable__head'>
            <div className='mcpCallsTable__cell'>{t('McpCallsColTime')}</div>
            <div className='mcpCallsTable__cell'>{t('McpCallsColBrand')}</div>
            <div className='mcpCallsTable__cell'>
              {t('McpCallsColPlatform')}
            </div>
            <div className='mcpCallsTable__cell'>{t('McpCallsColTool')}</div>
            <div className='mcpCallsTable__cell'>
              {t('McpCallsColDirection')}
            </div>
            <div className='mcpCallsTable__cell'>{t('McpCallsColStatus')}</div>
            <div className='mcpCallsTable__cell'>
              {t('McpCallsColDuration')}
            </div>
            <div className='mcpCallsTable__cell'>
              {t('McpCallsColEmployee')}
            </div>
          </div>

          {entries.length === 0 ? (
            <div className='mcpCallsTable__empty'>
              <TeachingEmptyState
                icon={databaseLargeSVG}
                title={t('McpCallsEmptyTitle')}
                body={t('McpCallsEmptyBody')}
                size='compact'
              />
            </div>
          ) : (
            entries.map((entry) => {
              const brandLabel =
                entry.shop_name ||
                shopOptions.find((s) => s.slug === entry.shop_slug)?.name ||
                entry.shop_slug ||
                (entry.shop_id != null ? String(entry.shop_id) : '—');
              const employeeLabel =
                entry.employee_email ||
                entry.employee_name ||
                (entry.employee_id != null
                  ? employeeEmailById[String(entry.employee_id)] ||
                    String(entry.employee_id)
                  : '—');
              return (
                <div className='mcpCallsTable__row' key={entry.id}>
                  <div
                    className='mcpCallsTable__cell mcpCallsTable__time'
                    data-label={t('McpCallsColTime')}
                  >
                    {formatTimestamp(entry.created_at)}
                  </div>
                  <div
                    className='mcpCallsTable__cell mcpCallsTable__brand'
                    data-label={t('McpCallsColBrand')}
                    title={brandLabel}
                  >
                    {brandLabel}
                  </div>
                  <div
                    className='mcpCallsTable__cell'
                    data-label={t('McpCallsColPlatform')}
                  >
                    {entry.platform ? (
                      <span className='mcpCallsChip'>{entry.platform}</span>
                    ) : (
                      '—'
                    )}
                  </div>
                  <div
                    className='mcpCallsTable__cell mcpCallsTable__tool'
                    data-label={t('McpCallsColTool')}
                    title={entry.tool_name || ''}
                  >
                    {entry.tool_name || '—'}
                  </div>
                  <div
                    className='mcpCallsTable__cell'
                    data-label={t('McpCallsColDirection')}
                  >
                    <StatusPill tone={directionTone(entry.direction)} withDot>
                      {t(`McpCallsDirection_${entry.direction}`, {
                        defaultValue: entry.direction,
                      })}
                    </StatusPill>
                  </div>
                  <div
                    className='mcpCallsTable__cell'
                    data-label={t('McpCallsColStatus')}
                  >
                    <StatusPill
                      tone={statusTone(entry.status)}
                      withDot
                      title={entry.error_code || undefined}
                    >
                      {t(`McpCallsStatus_${entry.status}`, {
                        defaultValue: entry.status,
                      })}
                    </StatusPill>
                  </div>
                  <div
                    className='mcpCallsTable__cell mcpCallsTable__duration'
                    data-label={t('McpCallsColDuration')}
                  >
                    {formatDuration(entry.duration_ms)}
                  </div>
                  <div
                    className='mcpCallsTable__cell mcpCallsTable__employee'
                    data-label={t('McpCallsColEmployee')}
                    title={employeeLabel}
                  >
                    {employeeLabel}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ---------- Pagination ---------- */}
      {entries.length > 0 && (
        <Pagination
          meta={meta}
          onPageChange={handlePageChange}
          loading={loading}
        />
      )}
    </div>
  );
};

export default McpCalls;
